import React, { useState } from 'react';
import { ArrowLeft, Users, Copy, Share2 } from 'lucide-react';
import api from '../lib/api';

export default function InviteFriends({ onBack }) {
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [stats, setStats] = useState(null);

  const inviteCode = 'RIGHT-EDWARD-2026';
  const inviteLink = `https://app.qureohealth.com/invite/${inviteCode}`;
  const inviteText = `Join me on Qureo and track your health: ${inviteLink}`;

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    if (type !== 'error') {
      window.setTimeout(() => {
        setFeedback((prev) => (prev.message === message ? { type: '', message: '' } : prev));
      }, 2800);
    }
  };

  const parseEmails = (input) => {
    return input
      .split(/[\s,;]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const loadStats = async () => {
    try {
      const res = await api.get('/api/referrals/mine');
      setStats(res.data?.stats || null);
    } catch (err) {
      // Keep the page usable even if stats are temporarily unavailable.
    }
  };

  React.useEffect(() => {
    loadStats();
  }, []);

  const handleCopy = async () => {
    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard not supported');
      }
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      showFeedback('success', 'Invite link copied to clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showFeedback('error', 'Could not copy automatically. Please copy the link manually.');
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
        title: 'Join me on Qureo',
        text: 'Track your health with me on Qureo',
        url: inviteLink,
        });
        showFeedback('success', 'Invite shared successfully.');
        return;
      }
      await handleCopy();
    } catch (err) {
      // User cancel in native share sheet should not show as an app failure
      if (String(err?.name || '').toLowerCase() === 'aborterror') return;
      showFeedback('error', 'Unable to share right now.');
    }
  };

  const openFallbackMailto = (recipients) => {
    const subject = encodeURIComponent('Join me on Qureo');
    const body = encodeURIComponent(
      `Hey,\n\nI am inviting you to join Qureo.\nUse this invite link:\n${inviteLink}\n\nInvite code: ${inviteCode}`
    );
    const to = encodeURIComponent(recipients.join(','));
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const handleSendInvite = async () => {
    const recipients = parseEmails(emailInput);

    if (recipients.length === 0) {
      showFeedback('error', 'Enter at least one email address.');
      return;
    }

    const invalid = recipients.filter((email) => !isValidEmail(email));
    if (invalid.length > 0) {
      showFeedback('error', `Invalid email: ${invalid[0]}`);
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/api/referrals/invite', {
        emails: recipients,
        channel: 'email',
      });

      const summary = res.data?.summary || {};
      const delivered = Number(summary.delivered || 0);
      const failed = Number(summary.failed || 0);

      if (delivered > 0 && failed === 0) {
        showFeedback('success', `Invite sent to ${delivered} recipient${delivered > 1 ? 's' : ''}.`);
      } else if (delivered > 0 && failed > 0) {
        showFeedback('success', `Sent to ${delivered}. ${failed} failed, opening fallback email draft.`);
        openFallbackMailto(recipients);
      } else {
        showFeedback('error', 'Delivery could not be confirmed. Opening email app fallback.');
        openFallbackMailto(recipients);
      }

      setEmailInput('');
      await loadStats();
    } catch (err) {
      showFeedback('error', 'Invite API failed. Opening email app fallback.');
      openFallbackMailto(recipients);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle}>
      {/* Back Button */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={onBack} style={backBtnStyle}>
          <ArrowLeft size={20} style={{ color: 'var(--primary)' }} />
        </button>
        <h1 style={headerStyle}>Invite Friends</h1>
      </div>

      {/* Hero Card */}
      <div style={heroCard}>
        <Users size={28} style={{ color: 'var(--primary )' }} />
        <div>
          <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>Share the wellness</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>
            Invite friends and earn rewards when they join.
          </div>
          {stats && (
            <div style={{ fontSize: '0.75rem', color: '#444', marginTop: 6 }}>
              Sent: {stats.totalInvites || 0} | Accepted: {stats.totalAccepted || 0}
            </div>
          )}
        </div>
      </div>

      {/* Invite Link */}
      <label style={labelStyle}>Your Invite Link</label>
      <div style={linkBox}>
        <span style={{ fontSize: '0.85rem', flex: 1 }}>{inviteLink}</span>
        <button onClick={handleCopy} style={iconBtnStyle}>
          <Copy size={16} />
        </button>
        <button onClick={handleShare} style={iconBtnStyle}>
          <Share2 size={16} />
        </button>
      </div>
      {copied && <div style={toastStyle}>Link copied to clipboard.</div>}

      {/* Invite by Email */}
      <label style={labelStyle}>Invite by Email</label>
      <input
        type="text"
        placeholder="Enter one or more emails (comma separated)"
        style={inputStyle}
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
      />
      <button style={buttonStyle} onClick={handleSendInvite} disabled={submitting}>
        {submitting ? 'Preparing...' : 'Send Invite'}
      </button>

      {feedback.message && (
        <div
          style={{
            ...toastStyle,
            color: feedback.type === 'error' ? '#b42318' : 'var(--primary)',
          }}
        >
          {feedback.message}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#666' }}>
        Tip: Use copy/share for messaging apps, or send invites by email from your default mail app.
      </div>
    </div>
  );
}

// Styles
const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const headerStyle = {
  fontSize: '1.25rem',
  fontWeight: '700',
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const heroCard = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 24,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const labelStyle = {
  fontSize: '0.85rem',
  marginTop: 16,
  marginBottom: 6,
  display: 'block',
};

const linkBox = {
  display: 'flex',
  alignItems: 'center',
  background: 'var(--surface)',
  border: '1px solid #ccc',
  borderRadius: 8,
  padding: '10px 12px',
  gap: 8,
};

const iconBtnStyle = {
  background: '#eee',
  border: 'none',
  borderRadius: 6,
  padding: 6,
  cursor: 'pointer',
};

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
  marginBottom: 12,
};

const buttonStyle = {
  padding: '10px 16px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const toastStyle = {
  marginTop: 8,
  fontSize: '0.85rem',
  color: 'var(--primary)',
};